/**
 * TesteDeFumacaForm.jsx
 * Formulário específico para o serviço de Teste de Fumaça.
 *
 * Props:
 *   value    → objeto com todos os dados do formulário
 *   onChange → (novoValor) => void
 *   showAdminUploads → exibe uploads extras (somente painel admin)
 */

import React, { useRef, useState } from 'react';
import {
  Box, Flex, Text, Heading, Button, IconButton, Icon,
  SimpleGrid, Select, Input, Checkbox, Stack,
  Image, Divider, Badge,
  Collapse, Spinner, useColorModeValue, useToast,
  Menu, MenuButton, MenuList, MenuItem,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdCameraAlt, MdClose, MdSearch, MdExpandMore, MdExpandLess, MdMyLocation, MdDownload } from 'react-icons/md';
import Card from 'components/card/Card.js';
import MapaCampoEditor, { MAPA_PADRAO } from './MapaCampoEditor';
import { geocodeAddress } from 'utils/geocode';

// ─── Opções ──────────────────────────────────────────────────────────────────
const OPT_TRAFEGO        = ['Normal', 'Alto', 'Médio', 'Baixo'];
const OPT_TIPO_SING      = ['PV', 'PI', 'TL'];
const OPT_LOCAL_SING     = ['Eixo', 'Passeio', 'Terço adjacente', 'Terço oposto', 'Passeio oposto'];
const OPT_PAVIMENTO      = ['Asfalto', 'Bloquete', 'Paralelepípedo', 'Concreto', 'Outros'];
const OPT_COND_SING      = [
  'Desnivelado', 'Coberto', 'Normal', 'Entulhado',
  'Vazamento de água', 'Desmoronando', 'Infiltração', 'Danificado', 'Outros',
];
const OPT_COND_BANCADA   = ['Afogada', 'Coberta', 'Normal', 'Quebrada', 'Sem fundo', 'Outros'];
const OPT_COND_CANALETA  = ['Afogada', 'Coberta', 'Normal', 'Quebrada', 'Sem fundo', 'Outros'];
const OPT_RESULTADO      = [
  'Normal', 'Conserto da canaleta', 'Lavagem e limpeza', 'Limpeza manual',
  'Nivelamento', 'Conserto do poço', 'Reconstrução', 'Trocar tampão',
  'Conserto da bancada', 'Outros',
];
const OPT_GALERIA_TIPO   = ['Inexistente', 'Existente parcial', 'Existente toda extensão'];
const OPT_GALERIA_POS    = ['Passeio adjacente', 'Terço adjacente', 'Eixo', 'Terço oposto', 'Passeio oposto'];
const OPT_IMOVEL_OCORR   = ['No passeio', 'No ralo', 'Na sarjeta', 'Na calha', 'Na caixa de águas pluviais', 'Outros'];
const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ─── Valores padrão ───────────────────────────────────────────────────────────
const singularidadePadrao = () => ({
  _id:             makeId(),
  numeroImovel:    '',
  pvAnteriorId:    '',   // referência manual para o PV anterior (usa _id da singularidade)
  tipoSing:        '',
  trafego:         '',
  localSing:       '',
  tipoPavimento:   '',
  condSing:        '',
  condBancada:     '',
  condCanaleta:    '',
  resultado:       [],
  pvNormal:        false,
  pvObstruido:     false,
  pvSujo:          false,
  pvCoberto:       false,
  pvTravado:       false,
  motivoNaoAberto: '',   // razão se pvCoberto ou pvTravado
  metragemAoPV:    '',   // distância deste PV ao PV anterior (m)
  ramaisAdicionais: [],  // array de ramais secundários: [{ _id, paraPVId, metragem, descricao }]
  redePos:         '',
  redeSitu:        '',
  redeOutros:      '',
  fotoInicio:      null,  // { preview, file, url, legenda }
  fotoFim:         null,
});

const ocorrenciaPadrao = () => ({
  _id:              makeId(),
  tipo:             '',  // 'galeria' | 'boca_lobo' | 'boca_leao' | 'imovel'
  galeriaTipo:      '',
  galeiraPositions: [],
  numeroImovel:     '',
  pvEntreDe:        '',
  pvEntreAte:       '',
  tipoOcorrencia:   '',
  tipoOutros:       '',
  descricao:        '',
  fotos:            [],  // sem limite de quantidade
});

export const FUMAÇA_PADRAO = () => ({
  singularidades:      [singularidadePadrao()],
  cep:                 '',
  endereco:            '',
  bairro:              '',
  complemento:         '',
  cidade:              '',
  estado:              '',
  lat:                 null,
  lng:                 null,
  fotoEnderecoInspecionado: null,
  fotoGoogleMaps:      null,
  ocorrencias:         [],  // [{ _id, tipo:'galeria'|'boca_lobo'|'boca_leao'|'imovel', fotos:[], ... }]
  mapaCampo:           null, // { nos, arestas } — editor digital
});

// ─── Reverse-geocode (Nominatim) ─────────────────────────────────────────────
async function reverseGeocode(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=pt`;
    const res = await fetch(url, { headers: { 'User-Agent': 'SGOS-PGSaneamento/1.0' } });
    if (!res.ok) return null;
    const d   = await res.json();
    const a   = d?.address || {};
    return {
      endereco: [a.road, a.pedestrian, a.path].find(Boolean) || '',
      bairro:   a.suburb || a.neighbourhood || a.city_district || a.quarter || '',
      cidade:   a.city   || a.town  || a.village || '',
      estado:   a.state_code?.slice(-2) || a.state || '',
    };
  } catch { return null; }
}

// ─── helpers ─────────────────────────────────────────────────────────────────
function SLabel({ children, required }) {
  const c = useColorModeValue('gray.600', 'gray.300');
  return (
    <Text fontSize="11px" fontWeight="700" textTransform="uppercase"
      letterSpacing=".04em" color={c} mb="4px">
      {children}{required && <Text as="span" color="red.400" ms="1">*</Text>}
    </Text>
  );
}

function SectionTitle({ children }) {
  const c = useColorModeValue('navy.700', 'white');
  return (
    <Text fontWeight="800" fontSize="xs" color={c}
      textTransform="uppercase" letterSpacing=".06em" mb="10px">
      {children}
    </Text>
  );
}

// Wrappers com fonte ≥16px no mobile (evita zoom automático no iOS)
function MInput({ inputBg, ...props }) {
  return (
    <Input
      bg={inputBg}
      borderRadius="8px"
      fontSize={{ base: '16px', md: '14px' }}
      h={{ base: '44px', md: '36px' }}
      {...props}
    />
  );
}
function MSelect({ inputBg, children, ...props }) {
  return (
    <Select
      bg={inputBg}
      borderRadius="8px"
      fontSize={{ base: '16px', md: '14px' }}
      h={{ base: '44px', md: '36px' }}
      {...props}
    >
      {children}
    </Select>
  );
}

// ─── Componente de foto individual ───────────────────────────────────────────
// NOTA: usamos dois inputs separados para garantir compatibilidade cross-device.
// Alguns Android (Samsung, Xiaomi, etc.) interpretam accept="image/*" sem
// capture como "abrir câmera", ignorando a galeria. Com dois inputs explícitos
// o OS trata cada intenção corretamente em qualquer dispositivo ou WebView.
function FotoUpload({ label, value, onChange, inputBg }) {
  const cameraRef  = useRef();
  const galleryRef = useRef();
  const subText    = useColorModeValue('gray.500', 'gray.400');
  const btnBorder  = useColorModeValue('gray.200', 'gray.600');

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    onChange({ file, preview: URL.createObjectURL(file), url: null, legenda: '' });
    e.target.value = '';
  };

  const downloadFoto = async (src, nome = 'foto.jpg') => {
    if (!src) return;
    // blob:/data: — foto local ainda não enviada ao servidor, download direto
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      const a = document.createElement('a');
      a.href = src; a.download = nome;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      return;
    }
    // URL remota (Firebase Storage) — tenta fetch com CORS
    // Requer que o bucket tenha CORS configurado: gsutil cors set cors.json gs://bucket
    try {
      const resp = await fetch(src, { mode: 'cors', credentials: 'omit' });
      if (!resp.ok) throw new Error('fetch failed');
      const blob   = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = nome;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    } catch {
      window.open(src, '_blank');
    }
  };

  return (
    <Box>
      <SLabel>{label}</SLabel>
      {/* Input câmera — capture força abertura direta da câmera */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }} onChange={handleFile} />
      {/* Input galeria — sem capture, usa o seletor de arquivos do SO */}
      <input ref={galleryRef} type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
        style={{ display: 'none' }} onChange={handleFile} />
      {value ? (
        <Box borderRadius="10px" overflow="hidden"
          border="1px solid" borderColor={btnBorder}>
          <Box position="relative">
            <Image src={value.preview || value.url}
              w="100%" h={{ base: '170px', md: '130px' }} objectFit="cover" />
            <Flex position="absolute" top="6px" right="6px" gap="4px">
              <IconButton
                icon={<Icon as={MdDownload} />} size="sm" colorScheme="blue"
                borderRadius="full" aria-label="Download foto"
                onClick={() => downloadFoto(value.preview || value.url, `${label}.jpg`)}
              />
              <IconButton
                icon={<Icon as={MdClose} />} size="sm" colorScheme="red"
                borderRadius="full" aria-label="Remover foto"
                onClick={() => onChange(null)}
              />
            </Flex>
          </Box>
          <Box p="8px">
            <MInput inputBg={inputBg} placeholder="Legenda..."
              h={{ base: '40px', md: '32px' }}
              fontSize={{ base: '16px', md: '13px' }}
              value={value.legenda || ''}
              onChange={(e) => onChange({ ...value, legenda: e.target.value })}
            />
          </Box>
        </Box>
      ) : (
        <Menu>
          <MenuButton
            as={Button}
            w="100%"
            h={{ base: '80px', md: '70px' }}
            variant="outline"
            borderStyle="dashed"
            borderWidth="2px"
            borderColor={btnBorder}
            borderRadius="10px"
            colorScheme="gray"
            _hover={{ borderColor: 'brand.400' }}
          >
            <Flex direction="column" align="center" gap="1">
              <Icon as={MdCameraAlt} boxSize="22px" color="gray.400" />
              <Text fontSize="11px" fontWeight="600">Adicionar foto</Text>
            </Flex>
          </MenuButton>
          <MenuList minW="160px">
            <MenuItem icon={<Icon as={MdCameraAlt} boxSize="18px" />}
              onClick={() => cameraRef.current?.click()}>
              Câmera
            </MenuItem>
            <MenuItem icon={<Icon as={MdAdd} boxSize="18px" />}
              onClick={() => galleryRef.current?.click()}>
              Galeria
            </MenuItem>
          </MenuList>
        </Menu>
      )}
    </Box>
  );
}

// ─── Card de uma Singularidade ────────────────────────────────────────────────
function SingularidadeCard({ data, idx, singularidades = [], onChange, onRemove, canRemove, inputBg }) {
  const cardBg    = useColorModeValue('gray.50', 'navy.900');
  const borderCol = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('navy.700', 'white');

  const set = (field, val) => onChange({ ...data, [field]: val });
  const anteriores = (singularidades || []).slice(0, idx);
  const defaultPrevId = anteriores.length > 0 ? (data.pvAnteriorId || anteriores[anteriores.length - 1]?._id || '') : '';
  const labelSing = (s, i) => {
    const identificador = s?.numeroImovel ? ` — ${s.numeroImovel}` : '';
    return `SING ${i + 1}${identificador}`;
  };

  return (
    <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
      {/* ── Cabeçalho ── */}
      <Flex justify="space-between" align="center" mb="12px">
        <Badge colorScheme="blue" borderRadius="full" px="3" fontSize="12px">
          {data.tipoSing ? `${data.tipoSing} ${idx + 1}` : `Singularidade ${idx + 1}`}
        </Badge>
        {canRemove && (
          <IconButton icon={<Icon as={MdDelete} />} size="sm" colorScheme="red"
            variant="ghost" aria-label="Remover" onClick={onRemove} />
        )}
      </Flex>

      {/* ── Identificador/singularidade + referência e metragem ao singular anterior ── */}
      <SimpleGrid columns={{ base: 1, sm: idx === 0 ? 1 : 3 }} gap="10px" mb="12px">
        <Box>
          <SLabel>Nº do Imóvel / Identificador</SLabel>
          <MInput inputBg={inputBg} value={data.numeroImovel}
            placeholder="Ex: 114" inputMode="numeric" onChange={(e) => set('numeroImovel', e.target.value)} />
        </Box>
        {idx > 0 && (
          <Box>
            <SLabel>Singularidade anterior</SLabel>
            <MSelect
              inputBg={inputBg}
              value={defaultPrevId}
              onChange={(e) => set('pvAnteriorId', e.target.value)}
              placeholder="Selecione"
            >
              {anteriores.map((s, i) => (
                <option key={s._id || i} value={s._id || ''}>
                  {labelSing(s, i)}
                </option>
              ))}
            </MSelect>
          </Box>
        )}
        {idx > 0 && (
          <Box>
            <SLabel>Metragem à anterior (m)</SLabel>
            <MInput inputBg={inputBg} type="number" min="0"
              value={data.metragemAoPV || ''}
              placeholder="Ex: 42" inputMode="numeric" onChange={(e) => set('metragemAoPV', e.target.value)} />
          </Box>
        )}
      </SimpleGrid>

      {/* ── Ramais adicionais (cruzados) — inline com metragem ── */}
      {idx > 0 && (
        <>
          <SectionTitle>Metragem adicional (singularidades cruzadas)</SectionTitle>
          {(data.ramaisAdicionais || []).map((ramal, rIdx) => (
            <Flex key={ramal._id} gap="8px" align="flex-end" mb="8px">
              <Box flex={1}>
                <SLabel>Singularidade de origem</SLabel>
                <MSelect
                  inputBg={inputBg}
                  value={ramal.paraPVId || ''}
                  onChange={(e) => {
                    const newRamais = [...(data.ramaisAdicionais || [])];
                    newRamais[rIdx] = { ...ramal, paraPVId: e.target.value };
                    set('ramaisAdicionais', newRamais);
                  }}
                  placeholder="Selecione"
                >
                  {singularidades.map((s, sIdx) => (
                    s._id !== data._id && (
                      <option key={s._id} value={s._id}>
                        {labelSing(s, sIdx)}
                      </option>
                    )
                  ))}
                </MSelect>
              </Box>
              <Box w="120px">
                <SLabel>Metragem (m)</SLabel>
                <MInput
                  inputBg={inputBg}
                  type="number"
                  min="0"
                  value={ramal.metragem || ''}
                  placeholder="Ex: 8"
                  onChange={(e) => {
                    const newRamais = [...(data.ramaisAdicionais || [])];
                    newRamais[rIdx] = { ...ramal, metragem: e.target.value };
                    set('ramaisAdicionais', newRamais);
                  }}
                />
              </Box>
              <Box flex={1}>
                <SLabel>Descrição (opcional)</SLabel>
                <MInput
                  inputBg={inputBg}
                  placeholder="Ex: Ramal da bancada"
                  value={ramal.descricao || ''}
                  onChange={(e) => {
                    const newRamais = [...(data.ramaisAdicionais || [])];
                    newRamais[rIdx] = { ...ramal, descricao: e.target.value };
                    set('ramaisAdicionais', newRamais);
                  }}
                />
              </Box>
              <IconButton
                icon={<Icon as={MdDelete} />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                aria-label="Remover ramal"
                onClick={() => {
                  set('ramaisAdicionais', (data.ramaisAdicionais || []).filter((_, j) => j !== rIdx));
                }}
              />
            </Flex>
          ))}
          <Button
            leftIcon={<Icon as={MdAdd} />}
            size="sm"
            variant="outline"
            colorScheme="blue"
            mb="12px"
            onClick={() => {
              set('ramaisAdicionais', [
                ...(data.ramaisAdicionais || []),
                { _id: makeId(), paraPVId: '', metragem: '', descricao: '' },
              ]);
            }}
          >
            Adicionar metragem adicional
          </Button>
          <Divider mb="12px" />
        </>
      )}

      <Divider mb="12px" />

      {/* ── Tabela de atributos: 1 col no celular, 2 no tablet, 4 no desktop ── */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="10px" mb="12px">
        <Box>
          <SLabel>Tipo Sing.</SLabel>
          <MSelect inputBg={inputBg} value={data.tipoSing}
            onChange={(e) => set('tipoSing', e.target.value)} placeholder="—">
            {OPT_TIPO_SING.map(o => <option key={o}>{o}</option>)}
          </MSelect>
        </Box>
        <Box>
          <SLabel>Tráfego</SLabel>
          <MSelect inputBg={inputBg} value={data.trafego}
            onChange={(e) => set('trafego', e.target.value)} placeholder="—">
            {OPT_TRAFEGO.map(o => <option key={o}>{o}</option>)}
          </MSelect>
        </Box>
        <Box>
          <SLabel>Local Sing.</SLabel>
          <MSelect inputBg={inputBg} value={data.localSing}
            onChange={(e) => set('localSing', e.target.value)} placeholder="—">
            {OPT_LOCAL_SING.map(o => <option key={o}>{o}</option>)}
          </MSelect>
        </Box>
        <Box>
          <SLabel>Tipo Pavimento</SLabel>
          <MSelect inputBg={inputBg} value={data.tipoPavimento}
            onChange={(e) => set('tipoPavimento', e.target.value)} placeholder="—">
            {OPT_PAVIMENTO.map(o => <option key={o}>{o}</option>)}
          </MSelect>
        </Box>
        <Box>
          <SLabel>Cond. Singularidade</SLabel>
          <MSelect inputBg={inputBg} value={data.condSing}
            onChange={(e) => set('condSing', e.target.value)} placeholder="—">
            {OPT_COND_SING.map(o => <option key={o}>{o}</option>)}
          </MSelect>
        </Box>
        <Box>
          <SLabel>Cond. Bancada</SLabel>
          <MSelect inputBg={inputBg} value={data.condBancada}
            onChange={(e) => set('condBancada', e.target.value)} placeholder="—">
            {OPT_COND_BANCADA.map(o => <option key={o}>{o}</option>)}
          </MSelect>
        </Box>
        <Box>
          <SLabel>Cond. Canaleta</SLabel>
          <MSelect inputBg={inputBg} value={data.condCanaleta}
            onChange={(e) => set('condCanaleta', e.target.value)} placeholder="—">
            {OPT_COND_CANALETA.map(o => <option key={o}>{o}</option>)}
          </MSelect>
        </Box>
        <Box gridColumn={{ sm: '1 / -1' }}>
          <SLabel>Resultado da Inspeção</SLabel>
          <SimpleGrid columns={{ base: 1, sm: 2 }} gap="6px" mt="4px">
            {OPT_RESULTADO.map(op => {
              const checked = (Array.isArray(data.resultado) ? data.resultado : []).includes(op);
              return (
                <Checkbox
                  key={op}
                  isChecked={checked}
                  colorScheme="orange"
                  size="md"
                  onChange={() => {
                    const arr = Array.isArray(data.resultado) ? data.resultado : [];
                    set('resultado', checked
                      ? arr.filter(v => v !== op)
                      : [...arr, op]
                    );
                  }}
                >
                  <Text fontSize="sm" color={textColor}>{op}</Text>
                </Checkbox>
              );
            })}
          </SimpleGrid>
        </Box>
      </SimpleGrid>

      <Divider mb="12px" />

      {/* ── Condições da Singularidade ── */}
      <SectionTitle>Condições da Singularidade</SectionTitle>
      <SimpleGrid columns={{ base: 1, sm: 2 }} gap="8px" mb="10px">
        {[
          ['pvNormal',   'Normal',   'green'],
          ['pvObstruido','Obstruído','red'],
          ['pvSujo',     'Sujo',     'orange'],
          ['pvCoberto',  'Coberto',  'orange'],
          ['pvTravado',  'Travado',  'orange'],
        ].map(([field, label, scheme]) => (
          <Checkbox key={field} isChecked={data[field]}
            onChange={(e) => set(field, e.target.checked)}
            colorScheme={scheme} size="lg">
            <Text fontSize="sm" color={textColor}>{label}</Text>
          </Checkbox>
        ))}
      </SimpleGrid>
      {(data.pvCoberto || data.pvTravado) && (
        <Box mb="14px">
          <SLabel>Motivo — não aberto</SLabel>
          <MInput inputBg={inputBg}
            placeholder="Ex: Veículo em cima, PV difícil de abrir..."
            value={data.motivoNaoAberto || ''}
            onChange={(e) => set('motivoNaoAberto', e.target.value)} />
        </Box>
      )}
      {!(data.pvCoberto || data.pvTravado) && <Box mb="4px" />}

      {/* ── Fotos: 1 coluna no celular, 2 no tablet ── */}
      <SectionTitle>Fotos</SectionTitle>
      <SimpleGrid columns={{ base: 1, sm: 2 }} gap="12px">
        <FotoUpload
          label={`Foto Início — ${data.tipoSing || 'Sing.'} ${idx + 1}`}
          value={data.fotoInicio}
          onChange={(v) => set('fotoInicio', v)}
          inputBg={inputBg}
        />
        <FotoUpload
          label={`Foto Final — ${data.tipoSing || 'Sing.'} ${idx + 1}`}
          value={data.fotoFim}
          onChange={(v) => set('fotoFim', v)}
          inputBg={inputBg}
        />
      </SimpleGrid>
    </Card>
  );
}

// ─── Card de Ocorrência ──────────────────────────────────────────────────────
function OcorrenciaCard({ data, idx, onChange, onRemove, inputBg }) {
  const cardBg    = useColorModeValue('gray.50', 'navy.900');
  const borderCol = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('navy.700', 'white');
  const subText   = useColorModeValue('gray.500', 'gray.400');
  const fotoRef   = useRef();

  const set = (field, val) => onChange({ ...data, [field]: val });

  const addFoto = (e) => {
    const novas = Array.from(e.target.files).map(f => ({
      file: f, preview: URL.createObjectURL(f), url: null, legenda: '',
    }));
    onChange({ ...data, fotos: [...(data.fotos || []), ...novas] });
    e.target.value = '';
  };
  const removeFoto = (i) =>
    onChange({ ...data, fotos: (data.fotos || []).filter((_, j) => j !== i) });
  const setLegenda = (i, leg) =>
    onChange({ ...data, fotos: (data.fotos || []).map((f, j) => j === i ? { ...f, legenda: leg } : f) });

  const downloadFotoOcorr = async (src, idx) => {
    if (!src) return;
    const nome = `ocorrencia_foto_${idx + 1}.jpg`;
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      const a = document.createElement('a');
      a.href = src; a.download = nome;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      return;
    }
    try {
      const resp = await fetch(src, { mode: 'cors', credentials: 'omit' });
      if (!resp.ok) throw new Error('fetch failed');
      const blob   = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = nome;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    } catch {
      window.open(src, '_blank');
    }
  };

  // NOTA: dois refs separados — mesmo motivo do FotoUpload (compatibilidade cross-device)
  const cameraOcorrRef  = useRef();

  const TIPO_OPTIONS = [
    { value: 'galeria',   label: 'Galeria de Águas Pluviais' },
    { value: 'boca_lobo', label: 'Boca de Lobo' },
    { value: 'boca_leao', label: 'Boca de Leão' },
    { value: 'imovel',    label: 'Imóvel' },
  ];
  const COLOR_MAP = { galeria: 'blue', boca_lobo: 'teal', boca_leao: 'green', imovel: 'purple' };
  const tipoLabel = TIPO_OPTIONS.find(o => o.value === data.tipo)?.label || `Ocorrência ${idx + 1}`;

  return (
    <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb="12px">
        <Badge colorScheme={COLOR_MAP[data.tipo] || 'gray'} borderRadius="full" px="2" fontSize="11px">
          {tipoLabel}
        </Badge>
        <IconButton icon={<Icon as={MdDelete} />} size="sm" colorScheme="red"
          variant="ghost" aria-label="Remover" onClick={onRemove} />
      </Flex>

      {/* Tipo */}
      <Box mb="14px">
        <SLabel required>Tipo de ocorrência</SLabel>
        <MSelect inputBg={inputBg} value={data.tipo || ''}
          onChange={(e) => set('tipo', e.target.value)} placeholder="Selecione o tipo">
          {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </MSelect>
      </Box>

      {/* ── Galeria de Águas Pluviais ── */}
      {data.tipo === 'galeria' && (
        <Stack spacing="10px" mb="14px">
          <Box>
            <SLabel>Existência</SLabel>
            <MSelect inputBg={inputBg} value={data.galeriaTipo || ''}
              onChange={(e) => set('galeriaTipo', e.target.value)} placeholder="Selecione">
              {OPT_GALERIA_TIPO.map(o => <option key={o}>{o}</option>)}
            </MSelect>
          </Box>
          {data.galeriaTipo && data.galeriaTipo !== 'Inexistente' && (
            <Box>
              <SLabel>Posição</SLabel>
              <Stack spacing="6px" mt="4px">
                {OPT_GALERIA_POS.map(pos => (
                  <Checkbox key={pos} colorScheme="blue" size="md"
                    isChecked={(data.galeiraPositions || []).includes(pos)}
                    onChange={(e) => {
                      const arr = data.galeiraPositions || [];
                      set('galeiraPositions', e.target.checked
                        ? [...arr, pos]
                        : arr.filter(p => p !== pos));
                    }}>
                    <Text fontSize="sm" color={textColor} lineHeight="1.4">{pos}</Text>
                  </Checkbox>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      {/* ── Imóvel ── */}
      {data.tipo === 'imovel' && (
        <Stack spacing="10px" mb="14px">
          <Box>
            <SLabel required>Nº do Imóvel</SLabel>
            <MInput inputBg={inputBg} placeholder="Ex: 114"
              value={data.numeroImovel || ''} onChange={(e) => set('numeroImovel', e.target.value)} />
          </Box>
          <SimpleGrid columns={2} gap="10px">
            <Box>
              <SLabel>Entre PV</SLabel>
              <MInput inputBg={inputBg} placeholder="PV nº..."
                value={data.pvEntreDe || ''} onChange={(e) => set('pvEntreDe', e.target.value)} />
            </Box>
            <Box>
              <SLabel>e PV</SLabel>
              <MInput inputBg={inputBg} placeholder="PV nº..."
                value={data.pvEntreAte || ''} onChange={(e) => set('pvEntreAte', e.target.value)} />
            </Box>
          </SimpleGrid>
          <Box>
            <SLabel>Tipo de Ocorrência — Extravasor</SLabel>
            <MSelect inputBg={inputBg} value={data.tipoOcorrencia || ''}
              onChange={(e) => set('tipoOcorrencia', e.target.value)} placeholder="Selecione">
              {OPT_IMOVEL_OCORR.map(o => <option key={o}>{o}</option>)}
            </MSelect>
            {data.tipoOcorrencia === 'Outros' && (
              <MInput mt="8px" inputBg={inputBg} placeholder="Descreva a ocorrência..."
                value={data.tipoOutros || ''} onChange={(e) => set('tipoOutros', e.target.value)} />
            )}
          </Box>
          <Box>
            <SLabel>Descrição</SLabel>
            <MInput inputBg={inputBg} placeholder="Descreva detalhes..."
              value={data.descricao || ''} onChange={(e) => set('descricao', e.target.value)} />
          </Box>
        </Stack>
      )}

      {/* ── Fotos (universal, sem limite de quantidade) ── */}
      {data.tipo && (
        <Box>
          {(data.tipo === 'galeria' || data.tipo === 'imovel') && <Divider mb="12px" />}
          <SLabel>Fotos</SLabel>
          {/* Input câmera: capture="environment" → abre câmera diretamente */}
          <input ref={cameraOcorrRef} type="file" accept="image/*" capture="environment"
            style={{ display: 'none' }} onChange={addFoto} />
          {/* Input galeria: MIME types explícitos → abre seletor de arquivos */}
          <input ref={fotoRef} type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
            multiple style={{ display: 'none' }} onChange={addFoto} />
          <Menu>
            <MenuButton
              as={Button}
              w="100%"
              size="md" h={{ base: '48px', md: '40px' }}
              variant="outline"
              colorScheme={COLOR_MAP[data.tipo] || 'brand'} borderRadius="12px"
              mb="10px">
              <Flex align="center" justify="center" gap="8px">
                <Icon as={MdCameraAlt} boxSize="18px" />
                <Text>Adicionar foto</Text>
              </Flex>
            </MenuButton>
            <MenuList minW="160px">
              <MenuItem icon={<Icon as={MdCameraAlt} boxSize="18px" />}
                onClick={() => cameraOcorrRef.current?.click()}>
                Câmera
              </MenuItem>
              <MenuItem icon={<Icon as={MdAdd} boxSize="18px" />}
                onClick={() => fotoRef.current?.click()}>
                Galeria
              </MenuItem>
            </MenuList>
          </Menu>
          {(data.fotos || []).length === 0 && (
            <Text fontSize="sm" color={subText} textAlign="center" py="2">
              Nenhuma foto adicionada.
            </Text>
          )}
          <Stack spacing="10px">
            {(data.fotos || []).map((f, i) => (
              <Box key={i} borderRadius="10px" overflow="hidden" border="1px solid" borderColor={borderCol}>
                <Box position="relative">
                  <Image src={f.preview || f.url} w="100%" h={{ base: '160px', md: '120px' }} objectFit="cover" />
                  <Flex position="absolute" top="6px" right="6px" gap="4px">
                    <IconButton icon={<Icon as={MdDownload} />} size="sm" colorScheme="blue"
                      borderRadius="full" aria-label="download"
                      onClick={() => downloadFotoOcorr(f.preview || f.url, i)} />
                    <IconButton icon={<Icon as={MdClose} />} size="sm" colorScheme="red"
                      borderRadius="full" aria-label="remover"
                      onClick={() => removeFoto(i)} />
                  </Flex>
                </Box>
                <Box px="8px" py="8px">
                  <Text fontSize="11px" color={subText} mb="4px">Foto {i + 1}</Text>
                  <MInput inputBg={inputBg} h={{ base: '40px', md: '32px' }}
                    fontSize={{ base: '16px', md: '13px' }}
                    placeholder="Legenda..." value={f.legenda || ''}
                    onChange={(e) => setLegenda(i, e.target.value)} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Card>
  );
}

// ─── Componente Principal ────────────────────────────────────────────────────
export default function TesteDeFumacaForm({ value, onChange, showAdminUploads = false }) {
  const inputBg   = useColorModeValue('white', 'whiteAlpha.100');
  const cardBg    = useColorModeValue('white', 'navy.800');
  const borderCol = useColorModeValue('gray.100', 'gray.600');
  const subText   = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('navy.700', 'white');
  const statBg    = useColorModeValue('gray.100', 'navy.900');
  const toast     = useToast();

  const [loadingCep,     setLoadingCep]     = useState(false);
  const [loadingGeo,     setLoadingGeo]     = useState(false);
  const [trechoExpanded, setTrechoExpanded] = useState(false);

  const data = value || FUMAÇA_PADRAO();
  const set  = (field, val) => onChange({ ...data, [field]: val });
  const setMulti = (fields) => onChange({ ...data, ...fields });

  // ── Busca CEP via ViaCEP → preenche endereço + bairro ──────────────────────
  const buscarCep = async () => {
    const cepLimpo = (data.cep || '').replace(/\D/g, '');
    if (cepLimpo.length !== 8) {
      toast({ title: 'CEP inválido.', status: 'warning', duration: 2000 }); return;
    }
    setLoadingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const d   = await res.json();
      if (d.erro) { toast({ title: 'CEP não encontrado.', status: 'error', duration: 2500 }); return; }

      const endereco = d.logradouro || data.endereco;
      const bairro   = d.bairro     || data.bairro;
      const cidade   = d.localidade || data.cidade;
      const estado   = d.uf         || data.estado;

      // Geocodifica o endereço para obter lat/lng
      const coords = await geocodeAddress({
        logradouro: endereco,
        bairro,
        cidade,
        uf:  estado,
        cep: data.cep,
      });

      setMulti({
        endereco,
        bairro,
        cidade,
        estado,
        cep:  data.cep,
        ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
      });

      toast({
        title:  coords ? 'Endereço e coordenadas preenchidos!' : 'Endereço preenchido!',
        description: coords
          ? `Lat: ${coords.lat.toFixed(5)} / Lng: ${coords.lng.toFixed(5)}`
          : 'Coordenadas não encontradas — use o GPS para melhor precisão.',
        status: 'success',
        duration: 3000,
      });
    } catch {
      toast({ title: 'Erro ao buscar CEP.', status: 'error', duration: 2500 });
    } finally {
      setLoadingCep(false);
    }
  };

  // ── GPS → reverse-geocode → preenche endereço ────────────────────────────────
  const pegarLocalizacao = () => {
    if (!navigator.geolocation) {
      toast({ title: 'GPS não disponível.', status: 'warning', duration: 2000 }); return;
    }
    setLoadingGeo(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lt, longitude: lg } = pos.coords;
        const addr = await reverseGeocode(lt, lg);
        setMulti({
          lat: lt,
          lng: lg,
          ...(addr?.endereco ? { endereco: addr.endereco } : {}),
          ...(addr?.bairro   ? { bairro:   addr.bairro   } : {}),
          ...(addr?.cidade   ? { cidade:   addr.cidade   } : {}),
          ...(addr?.estado   ? { estado:   addr.estado   } : {}),
        });
        setLoadingGeo(false);
        toast({
          title: addr ? 'Endereço auto-preenchido!' : 'GPS capturado!',
          status: 'success', duration: 2000,
        });
      },
      () => {
        setLoadingGeo(false);
        toast({ title: 'Não foi possível obter localização.', status: 'error', duration: 2500 });
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  // ── Sincroniza endereco → mapaCampo.ruaPrincipal ──────────────────────────────
  React.useEffect(() => {
    const rp = data.endereco || '';
    if (data.mapaCampo && data.mapaCampo.ruaPrincipal !== rp) {
      onChange({ ...data, mapaCampo: { ...data.mapaCampo, ruaPrincipal: rp } });
    }
  }, [data.endereco]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── helpers para singularidades ────────────────────────────────────────────
  const normalizarSingularidades = (lista) => {
    const base = (lista || []).map((s) => ({
      ...s,
      _id: s?._id ? String(s._id) : makeId(),
      pvAnteriorId: s?.pvAnteriorId ? String(s.pvAnteriorId) : '',
      resultado: Array.isArray(s?.resultado) ? s.resultado : (s?.resultado ? [s.resultado] : []),
    }));

    return base.map((s, idx) => {
      if (idx === 0) return { ...s, pvAnteriorId: '' };
      const anteriores = base.slice(0, idx);
      const atualValido = s.pvAnteriorId && anteriores.some(a => String(a._id) === String(s.pvAnteriorId));
      const padrao = anteriores[anteriores.length - 1]?._id || '';
      return { ...s, pvAnteriorId: atualValido ? String(s.pvAnteriorId) : String(padrao) };
    });
  };

  React.useEffect(() => {
    const lista = data.singularidades || [];
    if (!Array.isArray(lista) || lista.length === 0) return;
    const normalizada = normalizarSingularidades(lista);
    const mudou = normalizada.some((s, i) => {
      const atual = lista[i] || {};
      return (s._id !== atual._id) || (s.pvAnteriorId !== atual.pvAnteriorId);
    });
    if (mudou) set('singularidades', normalizada);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.singularidades]);

  const addSingularidade = () => {
    const lista = data.singularidades || [];
    const nova = singularidadePadrao();
    if (lista.length > 0) nova.pvAnteriorId = String(lista[lista.length - 1]?._id || '');
    set('singularidades', normalizarSingularidades([...lista, nova]));
  };

  const removeSingularidade = (i) => {
    const novaLista = (data.singularidades || []).filter((_, idx) => idx !== i);
    set('singularidades', normalizarSingularidades(novaLista));
  };

  const updateSingularidade = (i, updated) => {
    const novaLista = (data.singularidades || []).map((s, idx) => idx === i ? updated : s);
    set('singularidades', normalizarSingularidades(novaLista));
  };

  // ── helpers para ocorrências ──────────────────────────────────────────────
  const addOcorrencia = () =>
    set('ocorrencias', [...(data.ocorrencias || []), ocorrenciaPadrao()]);
  const removeOcorrencia = (i) =>
    set('ocorrencias', (data.ocorrencias || []).filter((_, idx) => idx !== i));
  const updateOcorrencia = (i, updated) =>
    set('ocorrencias', (data.ocorrencias || []).map((o, idx) => idx === i ? updated : o));

  // ─── Cálculos automáticos do trecho ────────────────────────────────────────
  const autoQtdPVs      = (data.singularidades || []).length;
  const autoQtdCobertos = (data.singularidades || []).filter(s => s.pvCoberto || s.pvTravado).length;
  const autoMetragem    = (data.singularidades || []).reduce((acc, s) => {
    const metPrincipal = parseFloat(s.metragemAoPV) || 0;
    const metAdicional = (s.ramaisAdicionais || []).reduce((sum, r) => sum + (parseFloat(r.metragem) || 0), 0);
    return acc + metPrincipal + metAdicional;
  }, 0);
  const autoImoveis  = (data.ocorrencias || []).filter(o => o.tipo === 'imovel').length;
  const autoBocaLobo = (data.ocorrencias || []).filter(o => o.tipo === 'boca_lobo').length;
  const autoBocaLeao = (data.ocorrencias || []).filter(o => o.tipo === 'boca_leao').length;
  const autoGaleria  = (data.ocorrencias || []).filter(o => o.tipo === 'galeria' && o.galeriaTipo && o.galeriaTipo !== 'Inexistente').length;
  const autoPvsSujos = (data.singularidades || []).filter(s => s.pvSujo).length;
  // conta singularidades com qualquer condição diferente de Normal (excluindo coberto/travado que já têm contador próprio)
  const autoSingAlerta = (data.singularidades || []).filter(s =>
    (s.condSing    && s.condSing    !== 'Normal') ||
    (s.condBancada && s.condBancada !== 'Normal') ||
    (s.condCanaleta && s.condCanaleta !== 'Normal') ||
    (s.redeSitu    && s.redeSitu    !== 'Normal') ||
    (Array.isArray(s.resultado) && s.resultado.length > 0 && !(s.resultado.length === 1 && s.resultado[0] === 'Normal'))
  ).length;

  return (
    <Stack spacing="20px">

      {/* ══ LOCALIZAÇÃO DO TRECHO ════════════════════════════════════════════ */}
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
        <Flex justify="space-between" align="center" mb="4px">
          <Heading size="xs" color={textColor} textTransform="uppercase" letterSpacing=".06em">
            Localização do Trecho
          </Heading>
          <Button size="sm" leftIcon={<Icon as={MdMyLocation} />}
            colorScheme="brand" variant="outline" borderRadius="8px"
            isLoading={loadingGeo} onClick={pegarLocalizacao}>
            GPS
          </Button>
        </Flex>
        <Text fontSize="11px" color={subText} mb="14px">
          Informe o CEP para buscar o endereço automaticamente, ou use o GPS como alternativa.
        </Text>

        {/* CEP */}
        <Box mb="10px">
          <SLabel required>CEP</SLabel>
          <Flex gap="8px">
            <MInput
              inputBg={inputBg}
              placeholder="00000-000"
              value={data.cep || ''}
              maxLength={9}
              inputMode="numeric"
              onChange={(e) => {
                let v = e.target.value.replace(/\D/g, '');
                if (v.length > 5) v = v.slice(0,5) + '-' + v.slice(5,8);
                set('cep', v);
              }}
              onKeyDown={(e) => e.key === 'Enter' && buscarCep()}
              flex="1"
            />
            <IconButton
              icon={loadingCep ? <Spinner size="xs" /> : <Icon as={MdSearch} />}
              aria-label="Buscar CEP" colorScheme="brand" borderRadius="8px"
              h={{ base: '44px', md: '36px' }} w={{ base: '44px', md: '36px' }}
              isLoading={loadingCep} onClick={buscarCep}
            />
          </Flex>
        </Box>

        {/* Endereço + Complemento */}
        <SimpleGrid columns={{ base: 1, sm: 2 }} gap="10px" mb="10px">
          <Box>
            <SLabel>Rua da rede / Endereço</SLabel>
            <MInput inputBg={inputBg}
              placeholder="Rua, Av., Praça..."
              value={data.endereco || ''}
              onChange={(e) => set('endereco', e.target.value)} />
          </Box>
          <Box>
            <SLabel>Complemento</SLabel>
            <MInput inputBg={inputBg}
              placeholder="Ap, bloco, refência..."
              value={data.complemento || ''}
              onChange={(e) => set('complemento', e.target.value)} />
          </Box>
        </SimpleGrid>

        {/* Bairro + Cidade + Estado */}
        <SimpleGrid columns={{ base: 1, sm: 3 }} gap="10px">
          <Box>
            <SLabel>Bairro</SLabel>
            <MInput inputBg={inputBg}
              placeholder="Nome do bairro"
              value={data.bairro || ''}
              onChange={(e) => set('bairro', e.target.value)} />
          </Box>
          <Box>
            <SLabel>Cidade</SLabel>
            <MInput inputBg={inputBg}
              placeholder="São Paulo..."
              value={data.cidade || ''}
              onChange={(e) => set('cidade', e.target.value)} />
          </Box>
          <Box>
            <SLabel>Estado (UF)</SLabel>
            <MInput inputBg={inputBg}
              placeholder="SP"
              maxLength={2}
              value={data.estado || ''}
              onChange={(e) => set('estado', e.target.value.toUpperCase())} />
          </Box>
        </SimpleGrid>
        {data.lat && data.lng && (
          <Text fontSize="11px" color="green.500" mt="8px">
            ✓ GPS: {Number(data.lat).toFixed(5)}, {Number(data.lng).toFixed(5)}
          </Text>
        )}

      </Card>

      {/* ══ UPLOADS ADMIN (somente Painel Admin) ═══════════════════════════ */}
      {showAdminUploads && (
        <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
          <Heading size="xs" color={textColor} textTransform="uppercase" letterSpacing=".06em" mb="6px">
            Endereço Inspecionado / Google Maps
          </Heading>
          <Text fontSize="11px" color={subText} mb="12px">
            Essas imagens aparecem no relatório abaixo da seção Resumo do Trecho.
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2 }} gap="12px">
            <FotoUpload
              label="Endereço Inspecionado"
              value={data.fotoEnderecoInspecionado}
              onChange={(v) => set('fotoEnderecoInspecionado', v)}
              inputBg={inputBg}
            />
            <FotoUpload
              label="Google Maps"
              value={data.fotoGoogleMaps}
              onChange={(v) => set('fotoGoogleMaps', v)}
              inputBg={inputBg}
            />
          </SimpleGrid>
        </Card>
      )}

      {/* ══ SINGULARIDADES ═══════════════════════════════════════════════════ */}
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
        <Heading size="xs" color={textColor} textTransform="uppercase" letterSpacing=".06em" mb="14px">
          PV / PI / TL Inspecionados
        </Heading>

        <Stack spacing="16px">
          {(data.singularidades || []).map((sing, i) => (
            <SingularidadeCard
              key={sing._id || i}
              data={sing}
              idx={i}
              singularidades={data.singularidades || []}
              onChange={(updated) => updateSingularidade(i, updated)}
              onRemove={() => removeSingularidade(i)}
              canRemove={(data.singularidades || []).length > 1}
              inputBg={inputBg}
            />
          ))}
        </Stack>

        <Button mt="16px" w="100%" size="md" h={{ base: '48px', md: '40px' }}
          leftIcon={<Icon as={MdAdd} />}
          colorScheme="brand" variant="outline" borderRadius="12px"
          onClick={addSingularidade}>
          Adicionar singularidade
        </Button>
      </Card>

      {/* ══ OCORRÊNCIAS ═══════════════════════════════════════════════════ */}
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
        <Heading size="xs" color={textColor} textTransform="uppercase" letterSpacing=".06em" mb="14px">
          Ocorrências
        </Heading>

        {(data.ocorrencias || []).length === 0 && (
          <Text fontSize="sm" color={subText} textAlign="center" py="2" mb="4px">
            Nenhuma ocorrência registrada. Toque em "Adicionar" para incluir galeria, boca de lobo/leão ou imóvel.
          </Text>
        )}

        <Stack spacing="14px">
          {(data.ocorrencias || []).map((oc, i) => (
            <OcorrenciaCard
              key={oc._id || i}
              data={oc}
              idx={i}
              onChange={(updated) => updateOcorrencia(i, updated)}
              onRemove={() => removeOcorrencia(i)}
              inputBg={inputBg}
            />
          ))}
        </Stack>

        <Button mt="16px" w="100%" size="md" h={{ base: '48px', md: '40px' }}
          leftIcon={<Icon as={MdAdd} />}
          colorScheme="brand" variant="outline" borderRadius="12px"
          onClick={addOcorrencia}>
          Adicionar ocorrência
        </Button>
      </Card>

      {/* ══ TRECHO PERCORRIDO ══════════════════════════════════════════════ */}
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
        <Heading size="xs" color={textColor} textTransform="uppercase"
          letterSpacing=".06em" mb="4px">
          Trecho Percorrido
        </Heading>
        <Text fontSize="11px" color={subText} mb="14px">
          Campos em cinza são calculados automaticamente a partir das singularidades e imóveis.
        </Text>

        {/* Totais automáticos */}
        <SimpleGrid columns={{ base: 2, md: 4 }} gap="10px" mb="14px">
          {[
            { label: 'Singularidades inspecionadas',      value: autoQtdPVs,                                    color: textColor },
            { label: 'Singularidades cobertas/travadas',  value: autoQtdCobertos > 0 ? autoQtdCobertos : '—',  color: autoQtdCobertos > 0 ? 'red.400' : textColor },
            { label: 'Singularidades sujas',              value: autoPvsSujos > 0 ? autoPvsSujos : '—',        color: autoPvsSujos > 0 ? 'orange.400' : textColor },
            { label: 'Sing. c/ problema',      value: autoSingAlerta > 0 ? autoSingAlerta : '—',    color: autoSingAlerta > 0 ? 'orange.400' : textColor },
            { label: 'Metragem aprox. (m)',    value: autoMetragem > 0 ? autoMetragem : '—',        color: textColor },
            { label: 'Imóveis c/ ocorrência',  value: autoImoveis > 0 ? autoImoveis : '—',          color: textColor },
            { label: 'Bocas de Lobo',          value: autoBocaLobo > 0 ? autoBocaLobo : '—',        color: textColor },
            { label: 'Bocas de Leão',          value: autoBocaLeao > 0 ? autoBocaLeao : '—',        color: textColor },
            { label: 'Galerias Pluviais',      value: autoGaleria  > 0 ? autoGaleria  : '—',        color: textColor },
          ].map(({ label, value, color }) => (
            <Box key={label} p="10px" borderRadius="10px" bg={statBg}
              border="1px solid" borderColor={borderCol} textAlign="center">
              <Text fontSize="18px" fontWeight="800" color={color} lineHeight="1.2">{value}</Text>
              <Text fontSize="10px" color={subText} mt="2px" textTransform="uppercase"
                letterSpacing=".04em">{label}</Text>
            </Box>
          ))}
        </SimpleGrid>

        {/* Expandir ocorrências registradas */}
        <Button variant="ghost" size="sm" w="100%"
          rightIcon={<Icon as={trechoExpanded ? MdExpandLess : MdExpandMore} />}
          onClick={() => setTrechoExpanded(v => !v)}
          color={subText} fontWeight="600" justifyContent="space-between">
          {trechoExpanded ? 'Ocultar' : 'Ver'} ocorrências registradas
        </Button>

        <Collapse in={trechoExpanded} animateOpacity>
          <Stack spacing="8px" mt="10px">

            {/* Ocorrências */}
            {(data.ocorrencias || []).map((oc, i) => {
              const TIPO_MAP = { galeria: 'Galeria', boca_lobo: 'Boca de Lobo', boca_leao: 'Boca de Leão', imovel: 'Imóvel' };
              const COLOR_MAP = { galeria: 'blue', boca_lobo: 'teal', boca_leao: 'green', imovel: 'purple' };
              return (
                <Flex key={oc._id || i} align="center" gap="10px" p="10px"
                  borderRadius="10px" bg={statBg} border="1px solid" borderColor={borderCol} wrap="wrap">
                  <Badge colorScheme={COLOR_MAP[oc.tipo] || 'gray'} borderRadius="full" px="2" flexShrink="0">
                    {TIPO_MAP[oc.tipo] || 'Ocorrência'} {i + 1}
                  </Badge>
                  {oc.tipo === 'imovel' && oc.numeroImovel && <Text fontSize="13px" color={textColor} fontWeight="700">Nº {oc.numeroImovel}</Text>}
                  {oc.tipo === 'imovel' && oc.pvEntreDe && oc.pvEntreAte && <Text fontSize="12px" color={subText}>PV {oc.pvEntreDe}→{oc.pvEntreAte}</Text>}
                  {oc.tipo === 'imovel' && oc.tipoOcorrencia && <Badge colorScheme="orange" borderRadius="full" px="2">{oc.tipoOcorrencia}</Badge>}
                  {oc.tipo === 'galeria' && oc.galeriaTipo && <Text fontSize="12px" color={textColor} fontWeight="600">{oc.galeriaTipo}</Text>}
                  {(oc.fotos || []).length > 0 && <Text fontSize="12px" color={subText}>{oc.fotos.length} foto(s)</Text>}
                </Flex>
              );
            })}

            {/* Singularidades */}
            {(data.singularidades || []).map((s, i) => (
              <Flex key={s._id || i} align="center" gap="10px" p="10px"
                borderRadius="10px" bg={statBg} border="1px solid" borderColor={borderCol} wrap="wrap">
                {/* badge principal usa tipoSing se preenchido */}
                <Badge colorScheme="blue" borderRadius="full" px="2" flexShrink="0">
                  {s.tipoSing ? `${s.tipoSing} ${i + 1}` : `PV ${i + 1}`}
                </Badge>
                {s.numeroImovel && (
                  <Text fontSize="12px" color={subText}>Nº {s.numeroImovel}</Text>
                )}
                {/* Resultado — só exibe itens diferentes de Normal */}
                {(Array.isArray(s.resultado) ? s.resultado : []).filter(r => r !== 'Normal').map(r => (
                  <Badge key={r} colorScheme="orange" borderRadius="full" px="2">{r}</Badge>
                ))}
                {/* Condições não-normais */}
                {s.condSing && s.condSing !== 'Normal' && (
                  <Badge colorScheme="yellow" borderRadius="full" px="2">Sing: {s.condSing}</Badge>
                )}
                {s.condBancada && s.condBancada !== 'Normal' && (
                  <Badge colorScheme="yellow" borderRadius="full" px="2">Bancada: {s.condBancada}</Badge>
                )}
                {s.condCanaleta && s.condCanaleta !== 'Normal' && (
                  <Badge colorScheme="yellow" borderRadius="full" px="2">Canaleta: {s.condCanaleta}</Badge>
                )}
                {/* Rede de esgoto — só exibe quando diferente do padrão */}
                {s.redeSitu && s.redeSitu !== 'Normal' && (
                  <Badge colorScheme="red" borderRadius="full" px="2">Rede: {s.redeSitu}</Badge>
                )}
                {/* Condições do PV */}
                {s.pvNormal    && <Badge colorScheme="green"  borderRadius="full" px="2">Normal</Badge>}
                {s.pvObstruido && <Badge colorScheme="red"    borderRadius="full" px="2">Obstruído</Badge>}
                {s.pvSujo      && <Badge colorScheme="yellow" borderRadius="full" px="2">Sujo</Badge>}
                {s.pvCoberto   && <Badge colorScheme="red"    borderRadius="full" px="2">Coberto</Badge>}
                {s.pvTravado   && <Badge colorScheme="red"    borderRadius="full" px="2">Travado</Badge>}
              </Flex>
            ))}

            {(data.ocorrencias || []).length === 0 && (data.singularidades || []).length === 0 && (
              <Text fontSize="13px" color={subText} textAlign="center" py="2">
                Nenhuma ocorrência registrada ainda.
              </Text>
            )}
          </Stack>
        </Collapse>
      </Card>

      {/* ══ MAPA DE CAMPO (EDITOR DIGITAL) ══ */}
      {(
      <Card bg={cardBg} p={{ base: '14px', md: '18px' }} borderWidth="1px" borderColor={borderCol}>
        <Heading size="xs" color={textColor} textTransform="uppercase"
          letterSpacing=".06em" mb="4px">
          Mapa de Campo
        </Heading>
        <Text fontSize="12px" color={subText} mb="14px">
          Reproduza o croqui da rede: adicione os PVs, informe as distâncias e as ruas de cruzamento.
        </Text>
        <MapaCampoEditor
          value={data.mapaCampo || { ...MAPA_PADRAO(), ruaPrincipal: data.endereco || '' }}
          onChange={(v) => set('mapaCampo', v)}
          initLat={data.lat ? parseFloat(data.lat) : undefined}
          initLng={data.lng ? parseFloat(data.lng) : undefined}
          singularidades={data.singularidades || []}
          ruaPrincipal={data.endereco || ''}
        />
      </Card>
      )}

    </Stack>
  );
}
